import { Badge, Button, Card } from '@rfz/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CloudUpload,
  Download,
  ExternalLink,
  FileCheck2,
  FileSpreadsheet,
  History,
  Loader2,
  MoreHorizontal,
  RefreshCw,
  RotateCcw,
  Search,
  Settings2,
  ShieldAlert,
  X,
} from 'lucide-react';
import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  confirmSpreadsheetImport,
  estimateWbSelectionDepth,
  exportInvalidImportRows,
  exportWbSelectionFiltered,
  getPreference,
  getWbSelectionDetail,
  getWbSelectionSummary,
  listImportBatches,
  retryWbSelectionStage,
  searchWbSelection,
  startWbSelectionDepth,
  updateWbSelectionDecisions,
  uploadSpreadsheet,
  type ImportInspection,
  type SpreadsheetField,
  type WbDepthEstimate,
  type WbDepthSelection,
  type WbSelectionRow,
  type WbSelectionView,
} from './api.js';

const importFields: Array<[SpreadsheetField, string, boolean]> = [
  ['keyword', '俄语关键词', true],
  ['chineseTranslation', '中文翻译', false],
  ['standardProductName', '标准商品名', false],
  ['category', 'WB 类目', false],
  ['currentDemand', '当前搜索量', false],
  ['searchVolumePrevious', '上期搜索量', false],
  ['clicks', '点击数', false],
  ['carts', '加购数', false],
  ['orderedItems', '订单数', false],
  ['orderConversionRate', '订单转化率', false],
  ['productsCount', '商品数量', false],
  ['dataDate', '数据日期', false],
];

const wbJamProfileFields = [
  ['searchKeywordRu', '搜索词'],
  ['searchVolumeCurrent', '搜索量'],
  ['searchVolumePrevious', '搜索量(上一周期)'],
  ['dailySearchVolumeCurrent', '日均搜索量'],
  ['dailySearchVolumePrevious', '日均搜索量(上一周期)'],
  ['wbCategoryWinnerZh', '类目订单冠军'],
  ['productClicksCurrent', '商品点击'],
  ['productClicksPrevious', '商品点击（同期对比）'],
  ['addToCartCurrent', '加入购物车'],
  ['addToCartPrevious', '加入购物车（上一周期）'],
  ['addToCartConversionCurrentPct', '加购转化率'],
  ['addToCartConversionPreviousPct', '加购转化率（上一周期）'],
  ['orderedItemsCurrent', '下单商品数量'],
  ['orderedItemsPrevious', '下单商品数量（上一周期）'],
  ['orderConversionCurrentPct', '下单转化率'],
  ['orderConversionPreviousPct', '下单转化率（上一周期）'],
  ['orderedProductsCurrent', '根据此搜索请求产生订单的商品数'],
  ['orderedProductsPrevious', '根据此搜索请求产生订单的商品数（上一周期）'],
  ['productsCountCurrent', '商品数量'],
  ['productsCountPrevious', '商品数量（上一周期）'],
] as const;

const decisionLabels: Record<string, string> = {
  priority_test: '优先测试',
  small_test: '可小批量测试',
  watch: '观察',
  reject: '不建议',
  risk_reject: '风险否决',
  data_incomplete: '待补全',
  processing_failed: '处理失败',
};

const preliminaryDecisionLabels: Record<string, string> = {
  preliminary_priority: '初筛优先',
  preliminary_enrich: '值得补全',
  preliminary_watch: '普通观察',
  preliminary_low: '低价值',
  preliminary_risk: '风险排除',
  preliminary_incomplete: '数据不足',
};

const enrichmentStageLabels: Record<string, string> = {
  jam_imported: 'Jam 已导入',
  preliminary_scored: '已完成初筛',
  entity_enriched: '实体已补全',
  topvisor_enriched: 'Topvisor 已补全',
  fully_enriched: '深度分析完成',
  rejected: '风险排除',
  failed: '补全失败',
};

const seasonLabels: Record<string, string> = {
  all_year_stable: '全年稳定',
  summer: '夏季',
  winter: '冬季',
  holiday: '节日型',
  school: '开学季',
  unknown: '不明确',
};

const statusLabels: Record<string, string> = {
  complete: '数据完整',
  data_incomplete: '待补全',
  processing: '处理中',
  pending: '等待处理',
  failed: '处理失败',
};

const riskLabels: Record<string, string> = {
  none: '未发现明显风险',
  review: '风险排除',
  high: '风险排除',
  restricted: '风险排除',
};

function simpleDecisionDisplayLabel(row: WbSelectionRow): string {
  if (row.hardReject || row.riskAction !== 'clear') return '风险排除';
  if (row.riskAction === 'clear') return '明确低风险';
  return '明确低风险';
}

const demandLevelLabels: Record<NonNullable<WbSelectionRow['demandLevel']>, string> = {
  high: '高',
  medium: '中',
  low: '低',
};

const conversionLevelLabels: Record<NonNullable<WbSelectionRow['conversionLevel']>, string> = {
  strong: '强',
  medium: '中',
  weak: '弱',
};

const competitionLevelLabels: Record<NonNullable<WbSelectionRow['competitionLevel']>, string> = {
  low_competition: '较低',
  normal_competition: '一般',
  crowded: '拥挤',
};

const categorySourceLabels: Record<string, string> = {
  imported: '导入文件',
  wb_jam: 'WB Jam 类目冠军',
  approved_mapping: '已批准映射',
  dictionary: '正式词库',
  ai_suggestion: 'AI 建议',
};

const fixedViews: Array<[WbSelectionView, string]> = [
  ['clear', '明确低风险'],
  ['risk_excluded', '风险词/禁售排除'],
  ['all', '全部候选'],
];

function riskDisplayLabel(row: WbSelectionRow) {
  if (row.hardReject || row.riskLevel === 'restricted') return '风险排除';
  if (
    row.riskLevel === 'review' ||
    row.riskLevel === 'high' ||
    row.riskMatchEvidence.some((item) => item.action === 'caution')
  )
    return '风险排除';
  return '未发现明显风险';
}

function supplyLevel(row: WbSelectionRow) {
  const percentile = row.supplyOpportunityPercentile;
  if (percentile == null) return { label: '数据不足', tone: 'muted' };
  if (percentile >= 0.7) return { label: '供给宽松', tone: 'positive' };
  if (percentile >= 0.3) return { label: '供需均衡', tone: 'balanced' };
  return { label: '竞争偏高', tone: 'negative' };
}

function sourcePeriod(row: WbSelectionRow) {
  const sourceFileName = unknownText(row.sourceSnapshot?.sourceFileName, '');
  const match = sourceFileName.match(/(\d{2}-\d{2}-\d{4}).*?(\d{2}-\d{2}-\d{4})/u);
  return match ? `${match[1]} 至 ${match[2]}` : '当前 WB Jam 导入周期';
}

const attributeLabels: Record<string, string> = {
  quantity: '数量',
  age: '年龄',
  size: '尺寸',
  color: '颜色',
  gender: '适用性别',
  useCase: '用途',
};

function numberText(value: number | null, digits = 0) {
  return value == null
    ? '—'
    : value.toLocaleString('zh-CN', {
        maximumFractionDigits: digits,
        minimumFractionDigits: digits,
      });
}

function percentText(value: number | null) {
  return value == null ? '—' : `${(value * 100).toFixed(1)}%`;
}

function unknownText(value: unknown, fallback = '') {
  return value == null
    ? fallback
    : typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
      ? String(value)
      : fallback;
}

function UploadWizard() {
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<number | null>(null);
  const [inspection, setInspection] = useState<ImportInspection | null>(null);
  const [mapping, setMapping] = useState<Partial<Record<SpreadsheetField, number | null>>>({});
  const [message, setMessage] = useState('');
  const upload = useMutation({
    mutationFn: (file: File) => uploadSpreadsheet('wb', file, setProgress),
    onMutate: () => {
      setMessage('正在识别文件并校验 SHA-256…');
      setProgress(0);
    },
    onSuccess: (result) => {
      setInspection(result);
      setMapping(
        Object.fromEntries(
          Object.entries(result.mapping).map(([field, value]) => [field, value.index]),
        ),
      );
      setProgress(null);
      setMessage('文件识别完成，请确认字段映射与数据预览');
    },
    onError: (error: Error) => {
      setMessage(error.message);
      setProgress(null);
    },
  });
  const confirm = useMutation({
    mutationFn: () => {
      if (!inspection) throw new Error('请先上传文件');
      if (mapping.keyword == null) throw new Error('必须映射俄语关键词列');
      return confirmSpreadsheetImport(
        inspection.uploadId,
        inspection.file.headerRowNumber,
        mapping,
      );
    },
    onSuccess: async (result) => {
      setMessage(`导入已开始（任务 ${result.jobId.slice(0, 8)}），任务中心会实时显示处理进度`);
      setInspection(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['jobs'] }),
        queryClient.invalidateQueries({ queryKey: ['wb-batches'] }),
        queryClient.invalidateQueries({ queryKey: ['wb-selection'] }),
        queryClient.invalidateQueries({ queryKey: ['wb-selection-summary'] }),
      ]);
    },
    onError: (error: Error) => setMessage(error.message),
  });

  return (
    <>
      <input
        ref={inputRef}
        hidden
        type="file"
        accept=".xlsx,.xls,.csv"
        onChange={(event) => {
          const file = event.currentTarget.files?.[0];
          if (file) upload.mutate(file);
          event.currentTarget.value = '';
        }}
      />
      <Button onClick={() => inputRef.current?.click()} disabled={upload.isPending}>
        {upload.isPending ? <Loader2 className="spin" size={17} /> : <CloudUpload size={17} />}
        {progress == null ? '上传 Excel' : `上传 ${Math.round(progress * 100)}%`}
      </Button>
      {message && <span className="upload-message">{message}</span>}
      {inspection && (
        <div className="wb-modal-backdrop" role="presentation">
          <section className="wb-import-modal" role="dialog" aria-modal="true">
            <header>
              <div>
                <p className="eyebrow">WB IMPORT</p>
                <h3>导入前验收预览</h3>
              </div>
              <button type="button" aria-label="关闭" onClick={() => setInspection(null)}>
                <X size={18} />
              </button>
            </header>
            <div className="wb-import-steps">
              {['文件识别', '字段映射', '数据预览', '确认导入'].map((label, index) => (
                <div className={index < 3 ? 'active' : ''} key={label}>
                  <span>{index + 1}</span>
                  <strong>{label}</strong>
                </div>
              ))}
            </div>
            <div className="wb-file-identification">
              <FileCheck2 size={22} />
              <div>
                <strong>{inspection.file.name}</strong>
                <small>
                  {(inspection.file.size / 1024 / 1024).toFixed(2)} MB · 表头第{' '}
                  {inspection.file.headerRowNumber} 行 · SHA-256 已校验
                  {inspection.file.selectedSheetName
                    ? ` · 工作表：${inspection.file.selectedSheetName}`
                    : ''}
                </small>
              </div>
            </div>
            <div className="wb-import-body">
              <section>
                <h4>
                  {inspection.wbJamProfile
                    ? `WB Jam 自动字段映射 · ${
                        Object.values(inspection.wbJamProfile.fieldMapping).filter(
                          (field) => field.index != null,
                        ).length
                      } / ${wbJamProfileFields.length}`
                    : '字段映射'}
                </h4>
                <div className="wb-mapping-grid">
                  {inspection.wbJamProfile
                    ? wbJamProfileFields.map(([field, label]) => {
                        const matched = inspection.wbJamProfile?.fieldMapping[field];
                        return (
                          <label key={field}>
                            <span>{label}</span>
                            <strong>
                              {matched?.index == null
                                ? '未识别'
                                : `${matched.column} · ${matched.header ?? '未命名列'}`}
                            </strong>
                          </label>
                        );
                      })
                    : importFields.map(([field, label, required]) => (
                        <label key={field}>
                          <span>
                            {label} {required && <em>必填</em>}
                          </span>
                          <select
                            value={mapping[field] ?? ''}
                            onChange={(event) =>
                              setMapping((current) => ({
                                ...current,
                                [field]:
                                  event.target.value === '' ? null : Number(event.target.value),
                              }))
                            }
                          >
                            <option value="">不导入</option>
                            {inspection.file.headers.map((header, index) => (
                              <option value={index} key={`${field}-${index}`}>
                                {index + 1}. {header || '未命名列'}
                              </option>
                            ))}
                          </select>
                        </label>
                      ))}
                </div>
                {inspection.wbJamProfile && (
                  <small>已按 WB Jam 配置自动识别；中文翻译和标准商品名不导入。</small>
                )}
              </section>
              <section>
                <h4>导入统计</h4>
                <div className="wb-import-counts">
                  {[
                    ['原始行数', inspection.counts.originalRows],
                    ['有效行数', inspection.counts.validRows],
                    ['空行', inspection.counts.emptyRows],
                    ['重复行', inspection.counts.duplicateRows],
                    ['已有记录', inspection.counts.existingRows],
                    ['新增记录', inspection.counts.newRows],
                    ['无法识别', inspection.counts.unrecognizedRows],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <small>{label}</small>
                      <strong>{Number(value).toLocaleString()}</strong>
                    </div>
                  ))}
                </div>
              </section>
            </div>
            {inspection.wbJamProfile && (
              <section className="wb-file-identification">
                <FileSpreadsheet size={22} />
                <div>
                  <strong>
                    {inspection.wbJamProfile.selectedSheetName} ·{' '}
                    {inspection.wbJamProfile.usedRange}
                  </strong>
                  <small>
                    选定周期：{inspection.wbJamProfile.selectedPeriod ?? '未提供'} · 对比周期：
                    {inspection.wbJamProfile.comparisonPeriod ?? '未提供'} · 类目范围：
                    {inspection.wbJamProfile.categoryScope.join('、') || '未提供'}
                  </small>
                </div>
              </section>
            )}
            {inspection.invalidReasonDistribution.length > 0 && (
              <section className="wb-invalid-summary">
                <header>
                  <div>
                    <h4>无效原因分布</h4>
                    <small>
                      共 {inspection.invalidRows.length.toLocaleString()}{' '}
                      条；无效记录不会进入候选批次。
                    </small>
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      const count = exportInvalidImportRows(inspection);
                      setMessage(`已导出 ${count.toLocaleString()} 条无效记录`);
                    }}
                  >
                    <Download size={15} />
                    导出无效行
                  </Button>
                </header>
                <div className="wb-import-counts">
                  {inspection.invalidReasonDistribution.map((reason) => (
                    <div key={reason.code}>
                      <small>{reason.label}</small>
                      <strong>{reason.count.toLocaleString()}</strong>
                      <span>{reason.code}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
            <section className="wb-preview-section">
              <h4>数据预览（前 {inspection.preview.length} 行）</h4>
              <div className="wb-preview-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>行号</th>
                      <th>识别</th>
                      <th>俄语关键词</th>
                      <th>中文翻译</th>
                      <th>类目</th>
                      <th>当前搜索量</th>
                      <th>商品数量</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inspection.preview.map((row) => (
                      <tr key={row.rowNumber}>
                        <td>{row.rowNumber}</td>
                        <td>{row.recognized ? '有效' : '需检查'}</td>
                        <td>{String(row.values.keyword ?? '—')}</td>
                        <td>{String(row.values.chineseTranslation ?? '—')}</td>
                        <td>{String(row.values.category ?? '—')}</td>
                        <td>{String(row.values.currentDemand ?? '—')}</td>
                        <td>{String(row.values.productsCount ?? '—')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
            <footer>
              <span>
                {confirm.error?.message ??
                  '当前仅预览；确认后新建独立批次，并按 batch + normalizedKeyword 去重'}
              </span>
              <Button variant="ghost" onClick={() => setInspection(null)}>
                取消
              </Button>
              <Button
                disabled={confirm.isPending || mapping.keyword == null}
                onClick={() => confirm.mutate()}
              >
                {confirm.isPending && <Loader2 className="spin" size={15} />}
                确认导入
              </Button>
            </footer>
          </section>
        </div>
      )}
    </>
  );
}

function SummaryCards({
  batchId,
  onFilter,
}: {
  batchId: string | null;
  onFilter: (filter: {
    decisions?: string[];
    dataStatuses?: string[];
    preliminaryDecisions?: string[];
    riskLevels?: string[];
    view?: WbSelectionView;
  }) => void;
}) {
  const query = useQuery({
    queryKey: ['wb-selection-summary', batchId],
    queryFn: () => getWbSelectionSummary(batchId),
    refetchInterval: 5000,
  });
  const data = query.data;
  const cards: Array<
    [
      string,
      number,
      {
        decisions?: string[];
        dataStatuses?: string[];
        preliminaryDecisions?: string[];
        riskLevels?: string[];
        view?: WbSelectionView;
      },
    ]
  > = [
    ['明确低风险', data?.riskClear ?? 0, { view: 'clear' }],
    ['风险词/禁售排除', data?.selectionRiskFiltered ?? 0, { view: 'risk_excluded' }],
    ['全部候选', data?.total ?? 0, { view: 'all' }],
  ];
  return (
    <div>
      <div className="wb-summary-grid">
        {cards.map(([label, value, filter]) => (
          <button type="button" key={label} onClick={() => onFilter(filter)}>
            <small>{label}</small>
            <strong>{value.toLocaleString()}</strong>
          </button>
        ))}
      </div>
      <p className="wb-summary-total">
        明确低风险 {(data?.riskClear ?? 0).toLocaleString()} / 风险词与禁售排除{' '}
        {(data?.selectionRiskFiltered ?? 0).toLocaleString()} / 总候选{' '}
        {data?.total.toLocaleString() ?? '0'}
      </p>
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="wb-detail-section">
      <h4>{title}</h4>
      {children}
    </section>
  );
}

function WbSelectionLegacyDetail({ row, onClose }: { row: WbSelectionRow; onClose: () => void }) {
  const detail = useQuery({
    queryKey: ['wb-selection-detail', row.id],
    queryFn: () => getWbSelectionDetail(row.id),
  });
  const candidate = detail.data?.candidate ?? row;
  const attributes = Object.entries(candidate.attributes ?? {}).filter(([, value]) =>
    Boolean(value),
  );
  return (
    <div className="drawer-backdrop" role="presentation" onMouseDown={onClose}>
      <aside
        className="side-panel wb-selection-detail"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <p className="eyebrow">KEYWORD DECISION</p>
            <h3>{candidate.originalKeywordRu}</h3>
            <p>{candidate.decisionReason ?? '数据处理中，尚未形成完整结论'}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="关闭详情">
            <X size={18} />
          </button>
        </header>
        <div className="wb-detail-scoreline">
          <span>
            <small>WB Jam 初筛分</small>
            <strong>
              {candidate.preliminaryScore == null
                ? '待初筛'
                : Math.round(candidate.preliminaryScore)}
            </strong>
          </span>
          <span>
            <small>最终机会分</small>
            <strong>
              {candidate.finalScore == null ? '待深度分析' : Math.round(candidate.finalScore)}
            </strong>
          </span>
          <span>
            <small>补全阶段</small>
            <strong>
              {enrichmentStageLabels[candidate.enrichmentStage] ?? candidate.enrichmentStage}
            </strong>
          </span>
          <span>
            <small>风险</small>
            <strong>{riskLabels[candidate.riskLevel] ?? candidate.riskLevel}</strong>
          </span>
        </div>
        <div className="wb-detail-content">
          <DetailSection title="WB Jam 初筛 · 指标、百分位与评分">
            <div className="wb-metric-grid">
              {[
                ['当前搜索量', candidate.searchVolumeCurrent],
                ['上期搜索量', candidate.searchVolumePrevious],
                ['点击数', candidate.clicks],
                ['加购数', candidate.carts],
                ['订单数', candidate.orderedItems],
                ['商品数量', candidate.productsCount],
                [
                  '需求百分位',
                  candidate.demandPercentile == null
                    ? null
                    : percentText(candidate.demandPercentile),
                ],
                [
                  '成交百分位',
                  candidate.conversionPercentile == null
                    ? null
                    : percentText(candidate.conversionPercentile),
                ],
                [
                  '供给机会百分位',
                  candidate.supplyOpportunityPercentile == null
                    ? null
                    : percentText(candidate.supplyOpportunityPercentile),
                ],
              ].map(([label, value]) => (
                <div key={label}>
                  <small>{label}</small>
                  <strong>
                    {typeof value === 'number' ? numberText(value, 2) : (value ?? '—')}
                  </strong>
                </div>
              ))}
            </div>
            <h3>
              {preliminaryDecisionLabels[candidate.preliminaryDecision] ??
                candidate.preliminaryDecision}
            </h3>
            <div className="wb-score-components">
              {Object.entries(candidate.preliminaryScoreBreakdown ?? {}).map(([key, value]) => (
                <div key={key}>
                  <span>{key}</span>
                  <strong>{value == null ? '—' : numberText(value, 2)}</strong>
                </div>
              ))}
            </div>
          </DetailSection>
          <DetailSection title="深度分析 · 原始关键词与标准实体">
            <dl className="wb-detail-list">
              <dt>原始关键词</dt>
              <dd>{candidate.originalKeywordRu}</dd>
              <dt>规范关键词</dt>
              <dd>{candidate.canonicalKeywordRu ?? '待识别'}</dd>
              <dt>标准实体</dt>
              <dd>{candidate.standardProductNameZh ?? '待识别'}</dd>
              <dt>商品角色</dt>
              <dd>{candidate.entityRole}</dd>
              <dt>实体置信度</dt>
              <dd>{percentText(candidate.entityConfidence)}</dd>
              <dt>聚类置信度</dt>
              <dd>{percentText(candidate.clusterConfidence)}</dd>
              <dt>聚类状态</dt>
              <dd>{candidate.clusterNeedsReview ? '低置信，保持独立并待复核' : '稳定实体聚类'}</dd>
              <dt>聚类搜索词数</dt>
              <dd>{detail.data?.cluster?.memberCount ?? '—'}</dd>
            </dl>
            {detail.data?.cluster?.longTailQueries?.length ? (
              <p className="wb-detail-note">
                长尾查询：{detail.data.cluster.longTailQueries.slice(0, 12).join('、')}
              </p>
            ) : null}
          </DetailSection>
          <DetailSection title="2. 中文翻译">
            <p>{candidate.chineseTranslationZh ?? '翻译待处理；其他可计算字段不会因此阻塞'}</p>
          </DetailSection>
          <DetailSection title="3. 属性、品牌和型号">
            <div className="detail-tags">
              {candidate.brand && <Badge className="badge-risk">品牌：{candidate.brand}</Badge>}
              {candidate.model && <Badge>型号：{candidate.model}</Badge>}
              {attributes.map(([key, value]) => (
                <Badge key={key}>
                  {attributeLabels[key] ?? key}：{String(value)}
                </Badge>
              ))}
              {!candidate.brand && !candidate.model && !attributes.length && (
                <span>未识别到结构化属性</span>
              )}
            </div>
          </DetailSection>
          <DetailSection title="4. WB 类目匹配">
            <dl className="wb-detail-list">
              <dt>类目</dt>
              <dd>{candidate.categoryNameZh ?? candidate.categoryNameRu ?? '待匹配'}</dd>
              <dt>来源</dt>
              <dd>
                {candidate.categorySource
                  ? (categorySourceLabels[candidate.categorySource] ?? candidate.categorySource)
                  : '—'}
              </dd>
              <dt>置信度</dt>
              <dd>{percentText(candidate.categoryConfidence)}</dd>
              <dt>确认状态</dt>
              <dd>{candidate.categoryNeedsReview ? '类目待确认' : '已确认'}</dd>
            </dl>
          </DetailSection>
          <DetailSection title="5. Topvisor 五种频次">
            <div className="wb-metric-grid">
              {[
                ['宽泛频次', candidate.frequencyBroad],
                ['短语频次', candidate.frequencyPhrase],
                ['固定词形', candidate.frequencyFixedForm],
                ['固定词序', candidate.frequencyFixedOrder],
                ['精确频次', candidate.frequencyExact],
              ].map(([label, value]) => (
                <div key={label}>
                  <small>{label}</small>
                  <strong>{numberText(value as number | null)}</strong>
                </div>
              ))}
            </div>
            <p className="wb-detail-note">查询状态：{candidate.frequencyStatus}</p>
          </DetailSection>
          <DetailSection title="6. WB Jam 搜索与成交漏斗">
            <div className="wb-metric-grid">
              {[
                ['当前搜索量', candidate.searchVolumeCurrent],
                ['上期搜索量', candidate.searchVolumePrevious],
                ['点击数', candidate.clicks],
                ['加购数', candidate.carts],
                [
                  '加购转化率',
                  candidate.cartConversionRate == null
                    ? null
                    : percentText(candidate.cartConversionRate),
                ],
                ['订单数', candidate.orderedItems],
                [
                  '订单转化率',
                  candidate.orderConversionRate == null
                    ? null
                    : percentText(candidate.orderConversionRate),
                ],
                ['成交商品数', candidate.orderedProducts],
                ['商品数量', candidate.productsCount],
              ].map(([label, value]) => (
                <div key={label}>
                  <small>{label}</small>
                  <strong>
                    {typeof value === 'number' ? numberText(value, 2) : (value ?? '—')}
                  </strong>
                </div>
              ))}
            </div>
            <p className="wb-detail-note">
              订单转化率来自该关键词行的「下单转化率」；库内保存小数，页面按小数 × 100%
              显示，不使用类目默认值。
            </p>
          </DetailSection>
          <DetailSection title="7. 搜索趋势与供需计算">
            <dl className="wb-detail-list">
              <dt>环比变化</dt>
              <dd>{percentText(candidate.demandTrendRate)}</dd>
              <dt>需求供给比</dt>
              <dd>{numberText(candidate.demandSupplyRatio, 4)}</dd>
              <dt>供需等级</dt>
              <dd>{supplyLevel(candidate).label}</dd>
              <dt>订单供给比</dt>
              <dd>{numberText(candidate.orderSupplyRatio, 4)}</dd>
              <dt>搜索订单效率</dt>
              <dd>{percentText(candidate.searchToOrderRate)}</dd>
              <dt>需求百分位</dt>
              <dd>{percentText(candidate.demandPercentile)}</dd>
              <dt>成交百分位</dt>
              <dd>{percentText(candidate.conversionPercentile)}</dd>
              <dt>供给机会百分位</dt>
              <dd>{percentText(candidate.supplyOpportunityPercentile)}</dd>
            </dl>
            <p className="wb-detail-note">
              需求供给比 = 当前搜索量 ÷ max(商品数量, 1)。分子是该关键词的 WB Jam
              当前搜索量，分母是该关键词对应商品数；数据周期：{sourcePeriod(candidate)}
              。精确值越高， 表示每个在售商品承接的搜索需求越多；主表等级使用 WB
              类目内供给机会百分位，不跨类目比绝对值。
            </p>
          </DetailSection>
          <DetailSection title="8. 季节性">
            <dl className="wb-detail-list">
              <dt>类型</dt>
              <dd>{seasonLabels[candidate.seasonalityType] ?? '不明确'}</dd>
              <dt>预计旺季</dt>
              <dd>
                {candidate.seasonalityType === 'unknown'
                  ? '不提供伪造月份'
                  : (candidate.estimatedPeakPeriod ?? '无明显季节性')}
              </dd>
              <dt>强度</dt>
              <dd>{numberText(candidate.seasonalityStrength, 2)}</dd>
              <dt>置信度</dt>
              <dd>{percentText(candidate.seasonalityConfidence)}</dd>
            </dl>
          </DetailSection>
          <DetailSection title="9. 风险判断">
            <h3>{riskLabels[candidate.riskLevel] ?? candidate.riskLevel}</h3>
            <p>{candidate.riskReason ?? '未发现明显风险'}</p>
            <dl className="wb-detail-list">
              <dt>命中规则 ID</dt>
              <dd>{candidate.riskRuleIds.length ? candidate.riskRuleIds.join('、') : '未命中'}</dd>
              <dt>风险组</dt>
              <dd>{candidate.riskGroups.length ? candidate.riskGroups.join('、') : '—'}</dd>
              <dt>来源</dt>
              <dd>{candidate.riskSourceIds.length ? candidate.riskSourceIds.join('、') : '—'}</dd>
              <dt>词库版本</dt>
              <dd>{candidate.riskDictionaryVersion ?? '—'}</dd>
              <dt>最后核验</dt>
              <dd>
                {candidate.riskVerifiedAt
                  ? new Date(candidate.riskVerifiedAt).toLocaleString('zh-CN', {
                      hour12: false,
                    })
                  : '—'}
              </dd>
            </dl>
            <div className="detail-tags">
              {candidate.riskTags.map((tag) => (
                <Badge className="badge-risk" key={tag}>
                  {tag}
                </Badge>
              ))}
            </div>
            {candidate.hardReject && (
              <p className="wb-hard-reject">
                <ShieldAlert size={15} /> 硬性风险已覆盖机会评分结论；原始评分仍保留用于审计。
              </p>
            )}
            <p className="wb-detail-note">
              词库判断用于 Wildberries 跨境选品预筛，不构成法律、海关、认证或平台最终结论。
            </p>
          </DetailSection>
          <DetailSection title="10. 深度分析评分组成">
            <div className="wb-score-components">
              {Object.entries(candidate.scoreComponents ?? {}).map(([key, value]) => (
                <div key={key}>
                  <span>{key}</span>
                  <strong>{numberText(value, 1)}</strong>
                </div>
              ))}
              {!Object.keys(candidate.scoreComponents ?? {}).length && (
                <p>尚未执行深度分析，最终分保持为空。</p>
              )}
            </div>
          </DetailSection>
          <DetailSection title="11. 选品结论及原因">
            <h3>
              {candidate.categoryNeedsReview
                ? '类目待确认'
                : candidate.finalDecision
                  ? (decisionLabels[candidate.finalDecision] ?? candidate.finalDecision)
                  : '待深度分析'}
            </h3>
            <p>
              {candidate.categoryNeedsReview
                ? '当前类目样本过少或来源置信度不足，保留原始分数供审计，确认类目前不输出高置信执行结论。'
                : candidate.finalDecision
                  ? (candidate.decisionReason ?? '已完成深度分析')
                  : '当前可先依据 WB Jam 初筛分决策；最终结论将在显式提交深度任务后生成。'}
            </p>
            <div className="wb-signal-columns">
              <div>
                <strong>正向信号</strong>
                {candidate.positiveSignals.map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
              <div>
                <strong>负向信号</strong>
                {candidate.negativeSignals.map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
              <div>
                <strong>缺失数据</strong>
                {candidate.missingData.map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </div>
          </DetailSection>
          <DetailSection title="12. 数据来源与字段更新时间">
            <pre>{JSON.stringify(candidate.fieldUpdatedAt, null, 2)}</pre>
          </DetailSection>
          <DetailSection title="13. 处理日志和失败原因">
            {candidate.failureCode && (
              <p className="wb-failure">
                <strong>{candidate.failureCode}</strong> · {candidate.errorSummary}
              </p>
            )}
            <div className="wb-event-list">
              {(detail.data?.events ?? candidate.processingLog ?? []).map((event, index) => (
                <div key={index}>
                  <strong>{unknownText(event.stage, 'pipeline')}</strong>
                  <span>{unknownText(event.status)}</span>
                  <small>{unknownText(event.summary ?? event.at)}</small>
                </div>
              ))}
              {!detail.data?.events.length && !candidate.processingLog?.length && (
                <p>暂无处理日志</p>
              )}
            </div>
          </DetailSection>
          <DetailSection title="14. 平台搜索入口">
            <div className="detail-actions">
              <a
                target="_blank"
                rel="noreferrer"
                href={`https://www.wildberries.ru/catalog/0/search.aspx?search=${encodeURIComponent(candidate.originalKeywordRu)}`}
              >
                Wildberries 搜索 <ExternalLink size={14} />
              </a>
            </div>
          </DetailSection>
        </div>
      </aside>
    </div>
  );
}

function WbSelectionSimpleDetail({ row, onClose }: { row: WbSelectionRow; onClose: () => void }) {
  const detail = useQuery({
    queryKey: ['wb-selection-detail', row.id],
    queryFn: () => getWbSelectionDetail(row.id),
  });
  const candidate = detail.data?.candidate ?? row;
  const decisionLabel = simpleDecisionDisplayLabel(candidate);
  return (
    <div className="drawer-backdrop" role="presentation" onMouseDown={onClose}>
      <aside
        className="side-panel wb-selection-detail"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <p className="eyebrow">WB 选品判断</p>
            <h3>{candidate.originalKeywordRu}</h3>
            <p>
              {candidate.chineseTranslationZh ??
                candidate.standardProductNameZh ??
                '中文商品待补充'}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="关闭详情">
            <X size={18} />
          </button>
        </header>
        <div className="wb-detail-content">
          <DetailSection title="1. 选品结论">
            <Badge className={`wb-decision wb-decision-${candidate.simpleDecision}`}>
              {decisionLabel}
            </Badge>
          </DetailSection>
          <DetailSection title="2. 结论原因">
            <p>{candidate.simpleDecisionReason ?? '尚未生成简单结论。'}</p>
            <div className="wb-signal-columns">
              <div>
                <strong>有利信号</strong>
                {candidate.simplePositiveSignals.length ? (
                  candidate.simplePositiveSignals.map((item) => <span key={item}>{item}</span>)
                ) : (
                  <span>—</span>
                )}
              </div>
              <div>
                <strong>不利信号</strong>
                {candidate.simpleNegativeSignals.length ? (
                  candidate.simpleNegativeSignals.map((item) => <span key={item}>{item}</span>)
                ) : (
                  <span>—</span>
                )}
              </div>
              <div>
                <strong>缺失数据</strong>
                {candidate.simpleMissingData.length ? (
                  candidate.simpleMissingData.map((item) => <span key={item}>{item}</span>)
                ) : (
                  <span>无</span>
                )}
              </div>
            </div>
          </DetailSection>
          <DetailSection title="3. 风险判断">
            <h3>{riskDisplayLabel(candidate)}</h3>
            <p>{candidate.riskReason ?? '未发现明显风险。'}</p>
            <dl className="wb-detail-list">
              <dt>风险组</dt>
              <dd>{candidate.riskGroups.length ? candidate.riskGroups.join('、') : '未命中'}</dd>
              <dt>命中规则</dt>
              <dd>{candidate.riskRuleIds.length ? candidate.riskRuleIds.join('、') : '未命中'}</dd>
              <dt>规则来源</dt>
              <dd>{candidate.riskSourceIds.length ? candidate.riskSourceIds.join('、') : '—'}</dd>
              <dt>最后核验</dt>
              <dd>
                {candidate.riskVerifiedAt
                  ? new Date(candidate.riskVerifiedAt).toLocaleString('zh-CN', { hour12: false })
                  : '—'}
              </dd>
              <dt>合规标签</dt>
              <dd>{candidate.riskTags.length ? candidate.riskTags.join('、') : '—'}</dd>
            </dl>
            <p className="wb-detail-note">
              命中风险词库即从选品池排除；未命中则按默认规则判为明确低风险。
            </p>
          </DetailSection>
          <DetailSection title="4. WB Jam 真实数据">
            <div className="wb-metric-grid">
              {[
                ['当前搜索量', numberText(candidate.searchVolumeCurrent)],
                ['上期搜索量', numberText(candidate.searchVolumePrevious)],
                ['搜索趋势', percentText(candidate.demandTrendRate)],
                ['点击数', numberText(candidate.clicks)],
                ['加购数', numberText(candidate.carts)],
                ['订单数', numberText(candidate.orderedItems)],
                ['订单转化率', percentText(candidate.orderConversionRate)],
                ['成交商品数', numberText(candidate.orderedProducts)],
                ['商品数量', numberText(candidate.productsCount)],
                ['需求供给比', numberText(candidate.demandSupplyRatio, 4)],
                ['订单供给比', numberText(candidate.orderSupplyRatio, 4)],
                ['搜索订单效率', percentText(candidate.searchToOrderRate)],
              ].map(([label, value]) => (
                <div key={label}>
                  <small>{label}</small>
                  <strong>{value}</strong>
                </div>
              ))}
            </div>
            <p className="wb-detail-note">
              需求供给比 = 当前搜索量 ÷ max(商品数量, 1)。分子是该关键词的 WB Jam
              当前搜索量，分母是该关键词对应商品数；数据周期：{sourcePeriod(candidate)}。数值越高，
              表示每个在售商品承接的搜索需求越多；等级仍按 WB 类目内比较。
            </p>
          </DetailSection>
          <DetailSection title="5. 需求、成交、竞争等级">
            <div className="wb-metric-grid">
              <div>
                <small>需求等级</small>
                <strong>
                  {candidate.demandLevel ? demandLevelLabels[candidate.demandLevel] : '数据不足'}
                </strong>
              </div>
              <div>
                <small>成交等级</small>
                <strong>
                  {candidate.conversionLevel
                    ? conversionLevelLabels[candidate.conversionLevel]
                    : '数据不足'}
                </strong>
              </div>
              <div>
                <small>竞争等级</small>
                <strong>
                  {candidate.competitionLevel
                    ? competitionLevelLabels[candidate.competitionLevel]
                    : '数据不足'}
                </strong>
              </div>
            </div>
            <p className="wb-detail-note">
              类目内前 30%：需求高、成交强、竞争较低；30% 至 70%：中等或一般；后
              30%：需求低、成交弱、竞争拥挤。不同类目不直接比较绝对值。
            </p>
          </DetailSection>
          <DetailSection title="6. 商品实体与 WB 类目">
            <dl className="wb-detail-list">
              <dt>原始关键词</dt>
              <dd>{candidate.originalKeywordRu}</dd>
              <dt>中文商品</dt>
              <dd>{candidate.chineseTranslationZh ?? '待补充'}</dd>
              <dt>标准商品实体</dt>
              <dd>{candidate.standardProductNameZh ?? candidate.canonicalKeywordRu ?? '待识别'}</dd>
              <dt>WB 类目</dt>
              <dd>{candidate.categoryNameZh ?? candidate.categoryNameRu ?? '待匹配'}</dd>
              <dt>类目来源</dt>
              <dd>
                {candidate.categorySource
                  ? (categorySourceLabels[candidate.categorySource] ?? '其他来源')
                  : '—'}
              </dd>
              <dt>类目状态</dt>
              <dd>{candidate.categoryNeedsReview ? '类目待确认' : '已确认'}</dd>
            </dl>
          </DetailSection>
          <DetailSection title="7. 季节性参考">
            <dl className="wb-detail-list">
              <dt>季节类型</dt>
              <dd>{seasonLabels[candidate.seasonalityType] ?? '不明确'}</dd>
              <dt>预计旺季</dt>
              <dd>
                {candidate.seasonalityType === 'unknown'
                  ? '暂无可靠月份趋势'
                  : (candidate.estimatedPeakPeriod ?? '无明显季节性')}
              </dd>
            </dl>
          </DetailSection>
          <DetailSection title="8. Wildberries 搜索入口">
            <div className="detail-actions">
              <a
                target="_blank"
                rel="noreferrer"
                href={`https://www.wildberries.ru/catalog/0/search.aspx?search=${encodeURIComponent(candidate.originalKeywordRu)}`}
              >
                在 Wildberries 搜索 <ExternalLink size={14} />
              </a>
            </div>
          </DetailSection>
          <DetailSection title="9. 数据来源与更新时间">
            <dl className="wb-detail-list">
              <dt>数据来源</dt>
              <dd>WB Jam 导入数据 + 已导入风险与排除词库</dd>
              <dt>数据周期</dt>
              <dd>{sourcePeriod(candidate)}</dd>
              <dt>结论更新时间</dt>
              <dd>
                {candidate.simpleDecisionUpdatedAt
                  ? new Date(candidate.simpleDecisionUpdatedAt).toLocaleString('zh-CN', {
                      hour12: false,
                    })
                  : '—'}
              </dd>
              <dt>记录更新时间</dt>
              <dd>{new Date(candidate.updatedAt).toLocaleString('zh-CN', { hour12: false })}</dd>
            </dl>
          </DetailSection>
        </div>
      </aside>
    </div>
  );
}

function WbSelectionDetail({ row, onClose }: { row: WbSelectionRow; onClose: () => void }) {
  const debug =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('debug') === 'true';
  return debug ? (
    <WbSelectionLegacyDetail row={row} onClose={onClose} />
  ) : (
    <WbSelectionSimpleDetail row={row} onClose={onClose} />
  );
}

function WbDepthDialog({
  batchId,
  selectedRows,
  text,
  preliminaryDecisions,
  riskLevels,
  onClose,
  onStarted,
}: {
  batchId: string;
  selectedRows: WbSelectionRow[];
  text: string;
  preliminaryDecisions: string[];
  riskLevels: string[];
  onClose: () => void;
  onStarted: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const [selection, setSelection] = useState<WbDepthSelection>({
    mode: selectedRows.length ? 'selected' : 'top',
    candidateIds: selectedRows.map((row) => row.id),
    text,
    preliminaryDecisions,
    riskLevels,
    categoryName: null,
    minPreliminaryScore: null,
    limit: 1000,
    concurrencyLimit: 2,
    runAi: true,
    runTopvisor: true,
  });
  const [estimate, setEstimate] = useState<WbDepthEstimate | null>(null);
  const changeSelection = (update: Partial<WbDepthSelection>) => {
    setSelection((current) => ({ ...current, ...update }));
    setEstimate(null);
  };
  const estimateMutation = useMutation({
    mutationFn: () => estimateWbSelectionDepth(batchId, selection),
    onSuccess: setEstimate,
  });
  const startMutation = useMutation({
    mutationFn: () => startWbSelectionDepth(batchId, selection),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['jobs'] });
      onStarted(
        `深度任务已创建：${result.estimate.uniqueEntityClusters.toLocaleString()} 个实体聚类，AI 最多 ${result.estimate.estimatedAiCalls.toLocaleString()} 次，Topvisor 最多 ${result.estimate.estimatedTopvisorCalls.toLocaleString()} 次`,
      );
      onClose();
    },
  });
  return (
    <div className="wb-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="wb-import-modal wb-depth-modal"
        role="dialog"
        aria-modal="true"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <p className="eyebrow">DEEP ENRICHMENT</p>
            <h3>选择深度处理范围</h3>
          </div>
          <button type="button" aria-label="关闭" onClick={onClose}>
            <X size={18} />
          </button>
        </header>
        <div className="wb-import-body">
          <section>
            <h4>处理范围</h4>
            <div className="wb-mapping-grid">
              <label>
                <span>范围</span>
                <select
                  value={selection.mode}
                  onChange={(event) =>
                    changeSelection({ mode: event.target.value as WbDepthSelection['mode'] })
                  }
                >
                  <option value="selected">当前选中记录（{selectedRows.length}）</option>
                  <option value="filtered">当前筛选结果</option>
                  <option value="top">按初筛排名取前 N 条</option>
                  <option value="category">指定 WB 类目</option>
                  <option value="threshold">达到初筛分阈值</option>
                </select>
              </label>
              <label>
                <span>批次上限</span>
                <select
                  value={selection.limit}
                  onChange={(event) =>
                    changeSelection({
                      limit: Number(event.target.value) as WbDepthSelection['limit'],
                    })
                  }
                >
                  {[100, 500, 1000, 2000].map((value) => (
                    <option value={value} key={value}>
                      前 {value} 条
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>并发上限</span>
                <select
                  value={selection.concurrencyLimit}
                  onChange={(event) =>
                    changeSelection({ concurrencyLimit: Number(event.target.value) })
                  }
                >
                  {[1, 2, 3, 4, 5].map((value) => (
                    <option value={value} key={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </label>
              {selection.mode === 'category' && (
                <label>
                  <span>WB 类目</span>
                  <input
                    value={selection.categoryName ?? ''}
                    onChange={(event) =>
                      changeSelection({ categoryName: event.target.value || null })
                    }
                    placeholder="输入类目名称"
                  />
                </label>
              )}
              {selection.mode === 'threshold' && (
                <label>
                  <span>最低初筛分</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={selection.minPreliminaryScore ?? ''}
                    onChange={(event) =>
                      changeSelection({
                        minPreliminaryScore: event.target.value ? Number(event.target.value) : null,
                      })
                    }
                    placeholder="例如 70"
                  />
                </label>
              )}
            </div>
            <div className="detail-tags">
              <label>
                <input
                  type="checkbox"
                  checked={selection.runAi}
                  onChange={(event) => changeSelection({ runAi: event.target.checked })}
                />{' '}
                按唯一实体聚类执行 AI
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={selection.runTopvisor}
                  onChange={(event) => changeSelection({ runTopvisor: event.target.checked })}
                />{' '}
                按 canonicalKeyword 去重 Topvisor
              </label>
            </div>
          </section>
          <section>
            <h4>执行前估算</h4>
            {!estimate && <p>先计算范围与缓存命中；不会在此步骤启动任何外部调用。</p>}
            {estimate && (
              <>
                <div className="wb-import-counts">
                  {[
                    ['批次候选', estimate.batchCandidateCount],
                    ['已完成初筛', estimate.preliminaryScoredCount],
                    ['已形成聚类', estimate.clusteredCandidateCount],
                    ['原始搜索词', estimate.rawSearchCount],
                    ['唯一实体聚类', estimate.uniqueEntityClusters],
                    ['预计 AI 审核', estimate.estimatedAiCalls],
                    ['Topvisor 去重词', estimate.estimatedTopvisorKeywords],
                    ['Topvisor 缓存', estimate.cachedTopvisorKeywords],
                    ['预计 Topvisor 调用', estimate.estimatedTopvisorCalls],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <small>{label}</small>
                      <strong>{Number(value).toLocaleString()}</strong>
                    </div>
                  ))}
                </div>
                {estimate.blockedReason && (
                  <p className="form-error" role="alert">
                    {estimate.blockedReason}
                  </p>
                )}
                <p className="wb-detail-note">{estimate.costNote}</p>
              </>
            )}
          </section>
        </div>
        <footer>
          <span>
            {estimateMutation.error?.message ??
              startMutation.error?.message ??
              estimate?.blockedReason ??
              '默认推荐初筛前 1000 条；低价值候选不会自动处理。'}
          </span>
          <Button variant="ghost" onClick={onClose}>
            取消
          </Button>
          <Button
            variant="secondary"
            disabled={estimateMutation.isPending}
            onClick={() => estimateMutation.mutate()}
          >
            {estimateMutation.isPending && <Loader2 className="spin" size={15} />}计算调用量
          </Button>
          <Button
            disabled={!estimate || startMutation.isPending || estimate.uniqueEntityClusters === 0}
            onClick={() => startMutation.mutate()}
          >
            {startMutation.isPending && <Loader2 className="spin" size={15} />}确认启动深度任务
          </Button>
        </footer>
      </section>
    </div>
  );
}

const allColumns = [
  ['translation', '中文商品'],
  ['category', 'WB 类目'],
  ['demand', '当前搜索量'],
  ['trend', '搜索趋势'],
  ['orders', '订单数'],
  ['conversion', '订单转化率'],
  ['products', '商品数量'],
  ['demandSupplyRatio', '需求供给比'],
  ['demandLevel', '需求等级'],
  ['conversionLevel', '成交等级'],
  ['competitionLevel', '竞争等级'],
  ['risk', '风险'],
  ['simpleDecision', '选品结论'],
  ['simpleReason', '结论原因'],
  ['entity', '标准商品实体'],
  ['seasonality', '季节性'],
  ['updatedAt', '更新时间'],
] as const;

const debugColumns = [
  ['preliminaryScore', '初筛分'],
  ['finalScore', '最终分'],
  ['preliminaryDecision', '初筛结论'],
  ['finalDecision', '最终结论'],
  ['completeness', '数据完整度'],
  ['status', '数据状态'],
] as const;

const defaultColumns = [
  'translation',
  'category',
  'demand',
  'trend',
  'orders',
  'conversion',
  'products',
  'demandSupplyRatio',
  'demandLevel',
  'conversionLevel',
  'competitionLevel',
  'risk',
  'simpleDecision',
  'simpleReason',
];

export function WbSelectionWorkbench() {
  const preference = useQuery({
    queryKey: ['preference', 'wb-selection-view'],
    queryFn: () => getPreference('wb-selection-view'),
  });
  if (preference.isLoading)
    return (
      <div className="table-state">
        <Loader2 className="spin" />
        正在恢复 WB 筛选视图…
      </div>
    );
  return <WbSelectionWorkbenchContent initialPreference={preference.data ?? {}} />;
}

function WbSelectionWorkbenchContent({
  initialPreference,
}: {
  initialPreference: Record<string, unknown>;
}) {
  const queryClient = useQueryClient();
  const debug =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('debug') === 'true';
  const selectableColumns = debug ? [...allColumns, ...debugColumns] : allColumns;
  const selectableColumnIds = new Set<string>(selectableColumns.map(([id]) => id));
  const [batchId, setBatchId] = useState<string | null>(null);
  const [searchDraft, setSearchDraft] = useState('');
  const [text, setText] = useState('');
  const [decisions, setDecisions] = useState<string[]>([]);
  const [preliminaryDecisions, setPreliminaryDecisions] = useState<string[]>([]);
  const [dataStatuses, setDataStatuses] = useState<string[]>([]);
  const [riskLevels, setRiskLevels] = useState<string[]>([]);
  const [view, setView] = useState<WbSelectionView>('clear');
  const [sortField, setSortField] = useState('recommended');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [minSearchVolume, setMinSearchVolume] = useState('');
  const [minOrderedItems, setMinOrderedItems] = useState('');
  const [minOrderConversionPct, setMinOrderConversionPct] = useState('');
  const [maxProductsCount, setMaxProductsCount] = useState('');
  const [minDemandSupplyRatio, setMinDemandSupplyRatio] = useState('');
  const [pageSize, setPageSize] = useState(50);
  const [offset, setOffset] = useState(0);
  const jumpPageRef = useRef<HTMLInputElement>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [detailRow, setDetailRow] = useState<WbSelectionRow | null>(null);
  const [showDepthDialog, setShowDepthDialog] = useState(false);
  const [message, setMessage] = useState('');
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    Array.isArray(initialPreference.visibleColumns) &&
      initialPreference.visibleColumns.includes('simpleDecision')
      ? initialPreference.visibleColumns.filter(
          (item): item is string => typeof item === 'string' && selectableColumnIds.has(item),
        )
      : defaultColumns,
  );
  const batches = useQuery({
    queryKey: ['wb-batches'],
    queryFn: listImportBatches,
    refetchInterval: 3000,
  });
  const wbBatches = (batches.data?.items ?? []).filter(
    (batch) => batch.platform === 'wb' && batch.status === 'ready',
  );
  const optionalNumber = (value: string, divisor = 1) => {
    if (!value.trim()) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed / divisor : null;
  };
  const queryInput = {
    batchId,
    text,
    decisions,
    dataStatuses,
    riskLevels,
    preliminaryDecisions,
    view,
    limit: pageSize,
    offset,
    sortField,
    sortDirection,
    minSearchVolume: optionalNumber(minSearchVolume),
    minOrderedItems: optionalNumber(minOrderedItems),
    minOrderConversionRate: optionalNumber(minOrderConversionPct, 100),
    maxProductsCount: optionalNumber(maxProductsCount),
    minDemandSupplyRatio: optionalNumber(minDemandSupplyRatio),
  };
  const query = useQuery({
    queryKey: ['wb-selection', queryInput],
    queryFn: () => searchWbSelection(queryInput),
    refetchInterval: 5000,
  });
  const totalRows = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const currentPage = Math.min(totalPages, Math.floor(offset / pageSize) + 1);
  useLayoutEffect(() => {
    if (tableScrollRef.current) tableScrollRef.current.scrollTop = 0;
  }, [offset]);
  const goToPage = (page: number) => {
    const safePage = Math.min(totalPages, Math.max(1, Math.trunc(page)));
    setOffset((safePage - 1) * pageSize);
    if (tableScrollRef.current) tableScrollRef.current.scrollTop = 0;
  };
  const effectiveBatchId = batchId ?? query.data?.batchId ?? null;
  const summary = useQuery({
    queryKey: ['wb-selection-summary', effectiveBatchId],
    queryFn: () => getWbSelectionSummary(effectiveBatchId),
    refetchInterval: 5000,
  });
  const currentBatch = wbBatches.find((batch) => batch.id === effectiveBatchId) ?? null;
  const batchImportActive = Boolean(
    currentBatch && ['queued', 'staging', 'applying'].includes(currentBatch.status),
  );
  const batchStatusText =
    currentBatch?.status === 'queued'
      ? '等待 worker 领取任务'
      : currentBatch?.status === 'staging'
        ? '正在读取并校验 Excel'
        : currentBatch?.status === 'applying'
          ? '正在写入候选并计算批次内指标'
          : currentBatch?.status === 'failed'
            ? '导入失败'
            : currentBatch?.status === 'cancelled'
              ? '导入已取消'
              : currentBatch?.status === 'succeeded'
                ? '导入完成'
                : (currentBatch?.status ?? '');
  const selectedRows = useMemo(
    () => (query.data?.items ?? []).filter((row) => selected[row.id]),
    [query.data?.items, selected],
  );
  const bulk = useMutation({
    mutationFn: (decision: 'keep' | 'discard' | 'unmarked') =>
      updateWbSelectionDecisions(selectedRows, decision),
    onSuccess: async (result) => {
      setMessage(
        `${result.updated.length} 条已标记${result.conflicts.length ? `，${result.conflicts.length} 条冲突` : ''}`,
      );
      setSelected({});
      await queryClient.invalidateQueries({ queryKey: ['wb-selection'] });
    },
    onError: (error: Error) => setMessage(error.message),
  });
  const retry = useMutation({
    mutationFn: (stage: 'all' | 'translation' | 'topvisor' | 'wb_match') => {
      if (!effectiveBatchId) throw new Error('当前没有可重试的批次');
      return retryWbSelectionStage(effectiveBatchId, stage);
    },
    onSuccess: async (result) => {
      setMessage(
        result.targetCount ? `已创建 ${result.targetCount} 条的重试任务` : '当前阶段没有失败记录',
      );
      await queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
    onError: (error: Error) => setMessage(error.message),
  });
  const exportMutation = useMutation({
    mutationFn: () => exportWbSelectionFiltered(queryInput),
    onSuccess: (count) => setMessage(`已导出当前筛选结果 ${count.toLocaleString()} 条`),
    onError: (error: Error) => setMessage(error.message),
  });
  const resetTransientState = (nextBatchId: string | null = batchId) => {
    setBatchId(nextBatchId);
    setSearchDraft('');
    setText('');
    setDecisions([]);
    setPreliminaryDecisions([]);
    setDataStatuses([]);
    setRiskLevels([]);
    setView('clear');
    setSortField('recommended');
    setSortDirection('desc');
    setMinSearchVolume('');
    setMinOrderedItems('');
    setMinOrderConversionPct('');
    setMaxProductsCount('');
    setMinDemandSupplyRatio('');
    setOffset(0);
    setSelected({});
    setDetailRow(null);
    setMessage('');
  };
  const setSummaryFilter = (filter: {
    decisions?: string[];
    dataStatuses?: string[];
    preliminaryDecisions?: string[];
    riskLevels?: string[];
    view?: WbSelectionView;
  }) => {
    setDecisions(filter.decisions ?? []);
    setDataStatuses(filter.dataStatuses ?? []);
    setPreliminaryDecisions(filter.preliminaryDecisions ?? []);
    setRiskLevels(filter.riskLevels ?? []);
    setView(filter.view ?? 'all');
    setOffset(0);
  };
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDirection((current) => (current === 'desc' ? 'asc' : 'desc'));
    else {
      setSortField(field);
      setSortDirection('desc');
    }
    setOffset(0);
  };
  const sortHeader = (label: string, field: string) => (
    <button
      type="button"
      className={sortField === field ? 'wb-sort-active' : undefined}
      onClick={() => toggleSort(field)}
      title={`按${label}${sortField === field && sortDirection === 'desc' ? '升序' : '降序'}排列`}
    >
      {label}
      {sortField !== field ? (
        <ArrowUpDown size={12} />
      ) : sortDirection === 'desc' ? (
        <ArrowDown size={12} />
      ) : (
        <ArrowUp size={12} />
      )}
    </button>
  );
  const show = (column: string) => visibleColumns.includes(column);
  const items = query.data?.items ?? [];
  const activeViewLabel = fixedViews.find(([candidate]) => candidate === view)?.[1] ?? '当前';
  const batchHasCandidates = (summary.data?.total ?? 0) > 0;

  return (
    <section className="workspace-panel wb-workbench">
      <header className="workspace-header">
        <div>
          <p className="eyebrow">WB KEYWORD DECISION WORKBENCH</p>
          <h2>WB 关键词级选品决策工作台</h2>
          <p>先排除风险词和禁售词；其余候选直接保留，再用 WB Jam 数据筛选和排序。</p>
        </div>
        <div className="header-actions">
          <UploadWizard />
        </div>
      </header>
      {currentBatch && (batchImportActive || currentBatch.status === 'failed') && (
        <div
          className={`wb-batch-state ${currentBatch.status === 'failed' ? 'failed' : 'active'}`}
          role="status"
        >
          {batchImportActive ? <Loader2 className="spin" size={18} /> : <ShieldAlert size={18} />}
          <div>
            <strong>{batchStatusText}</strong>
            <span>
              {currentBatch.originalFileName} · 批次 {currentBatch.id.slice(0, 8)}
            </span>
          </div>
          <small>
            {batchImportActive
              ? '导入完成前继续显示上一批数据；完成后一次性切换，无需重复上传。'
              : '请在任务中心查看失败原因并重试。'}
          </small>
        </div>
      )}
      <SummaryCards batchId={effectiveBatchId} onFilter={setSummaryFilter} />
      <nav className="wb-fixed-views" aria-label="WB 选品固定视图">
        {fixedViews.map(([id, label]) => (
          <button
            type="button"
            key={id}
            className={view === id ? 'active' : ''}
            onClick={() => {
              setView(id);
              setDecisions([]);
              setDataStatuses([]);
              setRiskLevels([]);
              setPreliminaryDecisions([]);
              setOffset(0);
            }}
          >
            {label}
          </button>
        ))}
      </nav>
      <Card className="workbench-card">
        <div className="table-toolbar wb-toolbar">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              setText(searchDraft);
              setOffset(0);
            }}
            className="search-box"
          >
            <Search size={17} />
            <input
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              placeholder="搜索俄语、中文、实体或类目"
            />
          </form>
          <select
            value={
              riskLevels.includes('review') &&
              riskLevels.includes('high') &&
              riskLevels.includes('restricted')
                ? 'risk'
                : (riskLevels[0] ?? '')
            }
            onChange={(event) => {
              setRiskLevels(
                event.target.value === 'risk'
                  ? ['review', 'high', 'restricted']
                  : event.target.value
                    ? [event.target.value]
                    : [],
              );
              setView('all');
              setOffset(0);
            }}
          >
            <option value="">全部风险</option>
            <option value="none">未发现明显风险</option>
            <option value="risk">风险词/禁售排除</option>
          </select>
          <Button
            size="sm"
            variant="ghost"
            className="wb-reset-filter"
            onClick={() => resetTransientState()}
          >
            <RotateCcw size={14} /> 重置筛选
          </Button>
        </div>
        <div className="wb-data-filters" aria-label="数据筛选">
          <strong>数据筛选</strong>
          {[
            ['搜索量 ≥', minSearchVolume, setMinSearchVolume, '0'],
            ['订单数 ≥', minOrderedItems, setMinOrderedItems, '0'],
            ['订单转化率 ≥ %', minOrderConversionPct, setMinOrderConversionPct, '0'],
            ['商品数量 ≤', maxProductsCount, setMaxProductsCount, '不限'],
            ['需求供给比 ≥', minDemandSupplyRatio, setMinDemandSupplyRatio, '0'],
          ].map(([label, value, setter, placeholder]) => (
            <label key={label as string}>
              <span>{label as string}</span>
              <input
                type="number"
                min="0"
                step="any"
                inputMode="decimal"
                value={value as string}
                placeholder={placeholder as string}
                onChange={(event) => {
                  (setter as (next: string) => void)(event.target.value);
                  setOffset(0);
                }}
              />
            </label>
          ))}
        </div>
        <div className="table-actions wb-table-actions">
          <span className="wb-result-count">
            {selectedRows.length
              ? `已选择 ${selectedRows.length} 条`
              : `筛选结果 ${query.data?.total?.toLocaleString() ?? 0} 条`}
          </span>
          <div className="wb-action-groups">
            {selectedRows.length > 0 && (
              <div className="wb-action-group wb-selection-actions">
                <Button size="sm" variant="secondary" onClick={() => bulk.mutate('keep')}>
                  批量保留
                </Button>
                <Button size="sm" variant="ghost" onClick={() => bulk.mutate('discard')}>
                  批量放弃
                </Button>
              </div>
            )}
            <label className="wb-batch-select">
              <span>
                <History size={14} /> 数据批次
              </span>
              <select
                value={batchId ?? ''}
                onChange={(event) => resetTransientState(event.target.value || null)}
              >
                <option value="">自动选择最新批次</option>
                {wbBatches.map((batch) => (
                  <option key={batch.id} value={batch.id}>
                    {new Date(batch.createdAt).toLocaleDateString('zh-CN')} ·{' '}
                    {batch.status === 'succeeded' ? '已完成' : batch.status} ·{' '}
                    {batch.originalFileName}
                  </option>
                ))}
              </select>
            </label>
            <div className="wb-action-group">
              <Button
                size="sm"
                disabled={!effectiveBatchId}
                onClick={() => setShowDepthDialog(true)}
              >
                <RefreshCw size={14} /> 深度分析
              </Button>
              <details className="wb-retry-menu">
                <summary>
                  <RefreshCw size={14} />
                  <span>失败重试</span>
                  <ChevronDown size={13} />
                </summary>
                <div>
                  <button onClick={() => retry.mutate('all')}>重试全部失败</button>
                  <button onClick={() => retry.mutate('translation')}>仅重试翻译失败</button>
                  <button onClick={() => retry.mutate('topvisor')}>仅重试 Topvisor</button>
                  <button onClick={() => retry.mutate('wb_match')}>仅重试 WB 匹配</button>
                </div>
              </details>
            </div>
            <div className="wb-action-group">
              <Button
                size="sm"
                variant="secondary"
                disabled={exportMutation.isPending}
                onClick={() => exportMutation.mutate()}
              >
                {exportMutation.isPending ? (
                  <Loader2 className="spin" size={14} />
                ) : (
                  <Download size={14} />
                )}{' '}
                导出结果
              </Button>
              <details className="column-settings">
                <summary>
                  <Settings2 size={14} />
                  <span>显示列</span>
                  <ChevronDown size={13} />
                </summary>
                <div>
                  {selectableColumns.map(([id, label]) => (
                    <label key={id}>
                      <input
                        type="checkbox"
                        checked={visibleColumns.includes(id)}
                        onChange={() =>
                          setVisibleColumns((current) =>
                            current.includes(id)
                              ? current.filter((item) => item !== id)
                              : [...current, id],
                          )
                        }
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </details>
            </div>
          </div>
        </div>
        {message && <p className="table-message">{message}</p>}
        {query.isLoading ? (
          <div className="table-state">
            <Loader2 className="spin" />
            正在读取 WB 批次候选…
          </div>
        ) : query.isError ? (
          <div className="table-state table-error">{query.error.message}</div>
        ) : !items.length ? (
          <div className="empty-state">
            <span>{batchImportActive ? <Loader2 className="spin" /> : <FileSpreadsheet />}</span>
            <h3>
              {batchImportActive
                ? '最新批次正在导入'
                : batchHasCandidates
                  ? `“${activeViewLabel}”视图当前为 0 条`
                  : '当前筛选下没有候选'}
            </h3>
            <p>
              {batchImportActive
                ? `${batchStatusText}，请勿重复上传；数据写入后会自动显示。`
                : batchHasCandidates
                  ? `本批次已有 ${(summary.data?.total ?? 0).toLocaleString()} 条候选；请调整数据筛选，或点击“全部候选”查看。`
                  : '上传 WB Jam Excel，或清除筛选条件后查看。'}
            </p>
          </div>
        ) : (
          <div ref={tableScrollRef} className="table-scroll wb-table-scroll">
            <table>
              <thead>
                <tr>
                  <th className="wb-sticky-select">
                    <input
                      type="checkbox"
                      checked={items.length > 0 && items.every((row) => selected[row.id])}
                      onChange={(event) =>
                        setSelected(
                          Object.fromEntries(items.map((row) => [row.id, event.target.checked])),
                        )
                      }
                    />
                  </th>
                  <th className="wb-sticky-keyword">
                    {sortHeader('候选关键词', 'originalKeywordRu')}
                  </th>
                  {show('translation') && (
                    <th className="wb-sticky-translation">
                      {sortHeader('中文商品', 'chineseTranslationZh')}
                    </th>
                  )}
                  {show('entity') && (
                    <th>{sortHeader('标准商品实体', 'standardProductNameZh')}</th>
                  )}
                  {show('category') && <th>{sortHeader('WB类目', 'categoryNameRu')}</th>}
                  {show('demand') && (
                    <th>{sortHeader('当前搜索量', 'searchVolumeCurrent')}</th>
                  )}
                  {show('trend') && <th>{sortHeader('搜索趋势', 'demandTrendRate')}</th>}
                  {show('orders') && (
                    <th>{sortHeader('订单数', 'orderedItems')}</th>
                  )}
                  {show('conversion') && (
                    <th>{sortHeader('订单转化', 'orderConversionRate')}</th>
                  )}
                  {show('products') && (
                    <th>{sortHeader('商品数量', 'productsCount')}</th>
                  )}
                  {show('demandSupplyRatio') && (
                    <th>{sortHeader('需求供给比', 'demandSupplyRatio')}</th>
                  )}
                  {show('demandLevel') && <th>{sortHeader('需求等级', 'demandPercentile')}</th>}
                  {show('conversionLevel') && (
                    <th>{sortHeader('成交等级', 'conversionPercentile')}</th>
                  )}
                  {show('competitionLevel') && (
                    <th>{sortHeader('竞争等级', 'supplyOpportunityPercentile')}</th>
                  )}
                  {show('seasonality') && <th>季节性</th>}
                  {show('risk') && <th>{sortHeader('风险', 'riskLevel')}</th>}
                  {show('simpleReason') && <th>结论原因</th>}
                  {show('preliminaryScore') && (
                    <th>{sortHeader('初筛分', 'preliminaryScore')}</th>
                  )}
                  {show('finalScore') && (
                    <th>{sortHeader('最终分', 'finalScore')}</th>
                  )}
                  {show('preliminaryDecision') && <th>初筛结论</th>}
                  {show('finalDecision') && <th>最终结论</th>}
                  {show('completeness') && (
                    <th>{sortHeader('完整度', 'enrichmentCompleteness')}</th>
                  )}
                  {show('status') && <th>数据状态</th>}
                  {show('updatedAt') && <th>{sortHeader('更新时间', 'updatedAt')}</th>}
                  {show('simpleDecision') && (
                    <th className="wb-sticky-simple-decision">
                      {sortHeader('选品结论', 'selectionEligibility')}
                    </th>
                  )}
                  <th className="wb-sticky-detail">详情</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => {
                  return (
                    <tr key={row.id}>
                      <td className="wb-sticky-select">
                        <input
                          type="checkbox"
                          checked={Boolean(selected[row.id])}
                          onChange={(event) =>
                            setSelected((current) => ({
                              ...current,
                              [row.id]: event.target.checked,
                            }))
                          }
                        />
                      </td>
                      <td className="wb-sticky-keyword">
                        <div className="keyword-cell">
                          <strong>{row.originalKeywordRu}</strong>
                          <span>
                            {row.brand
                              ? `品牌/IP：${row.brand}`
                              : (row.canonicalKeywordRu ?? row.normalizedKeyword)}
                          </span>
                        </div>
                      </td>
                      {show('translation') && (
                        <td className="wb-sticky-translation">
                          {row.chineseTranslationZh ?? row.standardProductNameZh ?? (
                            <span className="wb-pending">待翻译</span>
                          )}
                        </td>
                      )}
                      {show('entity') && (
                        <td>
                          {row.standardProductNameZh ?? <span className="wb-pending">待识别</span>}
                        </td>
                      )}
                      {show('category') && (
                        <td>
                          <div className="keyword-cell">
                            <strong>{row.categoryNameZh ?? row.categoryNameRu ?? '待匹配'}</strong>
                            <span>
                              {row.categoryNeedsReview
                                ? '类目待确认'
                                : `${categorySourceLabels[row.categorySource ?? ''] ?? row.categorySource ?? '来源未知'} · ${percentText(row.categoryConfidence)}`}
                            </span>
                          </div>
                        </td>
                      )}
                      {show('demand') && (
                        <td>
                          {row.searchVolumeCurrent == null ? (
                            <span className="wb-pending">数据不足</span>
                          ) : (
                            numberText(row.searchVolumeCurrent)
                          )}
                        </td>
                      )}
                      {show('trend') && (
                        <td
                          className={
                            row.demandTrendRate != null && row.demandTrendRate < 0
                              ? 'negative'
                              : 'positive'
                          }
                        >
                          {percentText(row.demandTrendRate)}
                        </td>
                      )}
                      {show('orders') && <td>{numberText(row.orderedItems)}</td>}
                      {show('conversion') && <td>{percentText(row.orderConversionRate)}</td>}
                      {show('products') && <td>{numberText(row.productsCount)}</td>}
                      {show('demandSupplyRatio') && (
                        <td title="当前搜索量 ÷ max(商品数量, 1)">
                          {numberText(row.demandSupplyRatio, 4)}
                        </td>
                      )}
                      {show('demandLevel') && (
                        <td>{row.demandLevel ? demandLevelLabels[row.demandLevel] : '数据不足'}</td>
                      )}
                      {show('conversionLevel') && (
                        <td>
                          {row.conversionLevel
                            ? conversionLevelLabels[row.conversionLevel]
                            : '数据不足'}
                        </td>
                      )}
                      {show('competitionLevel') && (
                        <td>
                          {row.competitionLevel
                            ? competitionLevelLabels[row.competitionLevel]
                            : '数据不足'}
                        </td>
                      )}
                      {show('seasonality') && (
                        <td>{seasonLabels[row.seasonalityType] ?? '不明确'}</td>
                      )}
                      {show('risk') && (
                        <td>
                          <Badge
                            className={
                              riskDisplayLabel(row) === '未发现明显风险' ? '' : 'badge-risk'
                            }
                          >
                            {riskDisplayLabel(row)}
                          </Badge>
                        </td>
                      )}
                      {show('simpleReason') && (
                        <td className="wb-decision-reason">{row.simpleDecisionReason ?? '—'}</td>
                      )}
                      {show('preliminaryScore') && (
                        <td>
                          {row.preliminaryScore == null ? (
                            <span className="wb-pending">数据不足</span>
                          ) : (
                            <span className="score-pill">{Math.round(row.preliminaryScore)}</span>
                          )}
                        </td>
                      )}
                      {show('finalScore') && (
                        <td>
                          {row.finalScore == null ? (
                            <span className="wb-pending">待深度分析</span>
                          ) : (
                            <span className="score-pill">{Math.round(row.finalScore)}</span>
                          )}
                        </td>
                      )}
                      {show('preliminaryDecision') && (
                        <td>
                          {preliminaryDecisionLabels[row.preliminaryDecision] ??
                            row.preliminaryDecision}
                        </td>
                      )}
                      {show('finalDecision') && (
                        <td>
                          {row.finalDecision ? (
                            (decisionLabels[row.finalDecision] ?? row.finalDecision)
                          ) : (
                            <span className="wb-pending">待深度分析</span>
                          )}
                        </td>
                      )}
                      {show('completeness') && <td>{percentText(row.enrichmentCompleteness)}</td>}
                      {show('status') && (
                        <td>
                          <span className={`wb-status wb-status-${row.dataStatus}`}>
                            {statusLabels[row.dataStatus] ?? row.dataStatus}
                          </span>
                          {row.failureCode && (
                            <small className="wb-row-error">{row.failureCode}</small>
                          )}
                        </td>
                      )}
                      {show('updatedAt') && (
                        <td>
                          {new Date(row.updatedAt).toLocaleString('zh-CN', {
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                      )}
                      {show('simpleDecision') && (
                        <td className="wb-sticky-simple-decision">
                          <Badge
                            className={`wb-decision ${
                              row.hardReject ||
                              row.riskAction !== 'clear'
                                ? 'wb-decision-risk_excluded'
                                : 'wb-decision-selectable'
                            }`}
                          >
                            {simpleDecisionDisplayLabel(row)}
                          </Badge>
                        </td>
                      )}
                      <td className="wb-sticky-detail">
                        <Button variant="ghost" size="sm" onClick={() => setDetailRow(row)}>
                          详情 <MoreHorizontal size={14} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <footer className="wb-pagination">
          <span className="wb-pagination-summary">
            共 {totalRows.toLocaleString()} 条
          </span>
          <label className="wb-page-size-select">
            <span className="sr-only">每页显示数量</span>
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setOffset(0);
              }}
            >
              {[20, 50, 100, 200].map((size) => (
                <option key={size} value={size}>
                  每页 {size} 条
                </option>
              ))}
            </select>
            <ChevronDown size={14} aria-hidden="true" />
          </label>
          <span className="wb-pagination-divider" aria-hidden="true" />
          <nav className="wb-pagination-controls" aria-label="分页导航">
            <Button
              className="wb-pagination-button"
              size="sm"
              variant="ghost"
              disabled={offset === 0}
              aria-label="上一页"
              title="上一页"
              onClick={() => goToPage(currentPage - 1)}
            >
              <ChevronLeft size={17} aria-hidden="true" />
            </Button>
            <span className="wb-pagination-status" aria-live="polite">
              第 <strong>{currentPage}</strong> / {totalPages} 页
            </span>
            <Button
              className="wb-pagination-button"
              size="sm"
              variant="ghost"
              disabled={currentPage >= totalPages}
              aria-label="下一页"
              title="下一页"
              onClick={() => goToPage(currentPage + 1)}
            >
              <ChevronRight size={17} aria-hidden="true" />
            </Button>
          </nav>
          <span className="wb-pagination-divider" aria-hidden="true" />
          <form
            className="wb-page-jump"
            onSubmit={(event) => {
              event.preventDefault();
              const requestedPage = Number(jumpPageRef.current?.value ?? currentPage);
              goToPage(Number.isFinite(requestedPage) ? requestedPage : currentPage);
            }}
          >
            <label htmlFor="wb-page-jump-input">跳转到</label>
            <input
              id="wb-page-jump-input"
              key={currentPage}
              ref={jumpPageRef}
              type="number"
              inputMode="numeric"
              min={1}
              max={totalPages}
              defaultValue={currentPage}
              onFocus={(event) => event.currentTarget.select()}
            />
            <Button className="wb-page-jump-submit" size="sm" variant="secondary" type="submit">
              前往
            </Button>
          </form>
        </footer>
      </Card>
      {detailRow && <WbSelectionDetail row={detailRow} onClose={() => setDetailRow(null)} />}
      {showDepthDialog && effectiveBatchId && (
        <WbDepthDialog
          batchId={effectiveBatchId}
          selectedRows={selectedRows}
          text={text}
          preliminaryDecisions={preliminaryDecisions}
          riskLevels={riskLevels}
          onClose={() => setShowDepthDialog(false)}
          onStarted={setMessage}
        />
      )}
    </section>
  );
}
